/* eslint-disable import/prefer-default-export */

/**
 * @typedef {import('../../gameFactory.types').ENGINES} ENGINES
 * @typedef {import('../../genericButton').GenericButtonOptions} GenericButtonOptions
 * @typedef {import('../../manager/audioManager.types').SOUNDS} SOUNDS
 * @typedef {import('../../manager/audioManager.types').AudioManagerOptions} AudioManagerOptions
 * @typedef {import('../../manager/popUpManager').PopUpManagerOptions} PopUpManagerOptions
 * @typedef {import('../../manager/deepLinkManager').DeepLinkManagerOptions} DeepLinkManagerOptions
 *
 *
 * @typedef {object} ArcadeOptions
 * @property {string} contentUrl The url for the JSON data file to load
 * @property {string} [arcadeName] The name (aka ID) of the arcade used for analytics and database purposes
 * @property {boolean} [disableHeader] Flag to indicate if the header should be ommited from the arcade
 * @property {string} [accountVariant] A modifier to use for tracking different accounts on the same device
 * @property {boolean} [cognitoCheckDisabled] A flag that idicate if the arcade should check for cognito user data upon launch (Performance optimization: checking can be very heavy on low power devices that don't need it)
 * @property {string} [skin] A space seperated string of names of css skins to be loaded dynamically at runtime
 * @property {string} [skinModules] A space seperated string of class names that should be added to the body of the arcade for special styling
 * @property {string} [entitlementEntity] The value to be used for checking if a user has any entitlments for a particular platform (aka cable provider)
 * @property {AudioManagerOptions} [audioManager] Data to initialize the AudioManager
 * @property {SOUNDS} [bgAudio] Id of default audio to play in the background
 * @property {object} [helpPopUp] Data to initialize the help button
 * @property {PopUpManagerOptions} [popUpManager] Data to initialize the help button
 * @property {(PageOptions | GenericButtonOptions)[]} [pages] The pages for the arcade
 * @property {DeepLinkManagerOptions} [deepLinkManager] Data to initialize the help button
 * @property {object} [sprites] Data to initialize the Sprite Factory
 * @property {string} [headerImage] URL of the image to display in the header
 * @property {string} [arcadeBg] The url to use as the default background media
 *
 *
 * @typedef {object} PageOptions
 * @property {string} [id]
 * @property {string} [route]
 * @property {string} [name] The name of the page. Also used for the page Title.
 * @property {ENGINES} [type] The engine identifier
 * @property {function} [engine] A custom class to instantiate for the page.
 * @property {string} [headerImage] URL of the image to display in the header
 * @property {string} [pageBg] URL of the image to display for he arcade background while on this page
 * @property {SOUNDS} [bgAudio] Id of audio to play for this page
 * @property {string[]|PageOptions[]|GenericButtonOptions[]} [pages]
 * @property {string[]|PageOptions[]|GenericButtonOptions[]} [visiblePages] The pages that should appear in the header
 * @property {string[]|PageOptions[]|GenericButtonOptions[]} [availablePages] Pages that
 * @property {boolean} [showInfoButton=false]
 */

/**
 * @enum {typeof ATTRIBUTES[keyof typeof ATTRIBUTES]}
 */
export const ATTRIBUTES = Object.freeze({
	accountVariant: 'accountVariant',
	analytics: 'analytics',
	arcadeBg: 'arcadeBg',
	arcadeName: 'arcadeName',
	audioManager: 'audioManager',
	bgAudio: 'bgAudio',
	cognitoCheckDisabled: 'cognitoCheckDisabled',
	contentUrl: 'contentUrl',
	deepLinkManager: 'deepLinkManager',
	disableHeader: 'disableHeader',
	entitlementEntity: 'entitlementEntity',
	helpPopUp: 'helpPopUp',
	pages: 'pages',
	popUpManager: 'popUpManager',
	skin: 'skin',
	skinModules: 'skinModules',
	sprites: 'sprites',
});

/**
 * @enum {typeof ARCADE_EVENTS[keyof typeof ARCADE_EVENTS]}
 */
export const ARCADE_EVENTS = Object.freeze({
	initialized: 'initialized',
});

/**
 * @enum {typeof HUB_EVENTS[keyof typeof HUB_EVENTS]}
 */
export const HUB_EVENTS = Object.freeze({
	returnToHub: 'returnToHub',
	finishedLoading: 'finishedLoading',
});
